import { apiFetchJson } from '../http/apiFetch';
import type {
  PagedSecurityAlerts,
  SecurityAlertRow,
  SecurityAlertStatus,
  SecurityDashboard,
  SecurityDashboardFilters,
  SecurityDashboardTotals,
  SecurityDashboardTrendPoint,
} from '../../models/securityDashboard';

function asString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value);
}

function asOptionalString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const str = String(value).trim();
  return str === '' ? null : str;
}

function asNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asRecord(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object') {
    return {};
  }
  const entries = value as Record<string, unknown>;
  return Object.entries(entries).reduce<Record<string, number>>((acc, [key, val]) => {
    acc[key] = asNumber(val);
    return acc;
  }, {});
}

function asAlertStatus(value: unknown): 'active' | 'resolved' {
  const str = asString(value).trim().toLowerCase();
  return str === 'resolved' ? 'resolved' : 'active';
}

function normalizeTotals(value: unknown): SecurityDashboardTotals {
  const t = (value && typeof value === 'object' ? (value as Record<string, unknown>) : {}) as Record<
    string,
    unknown
  >;
  return {
    failedLogins: asNumber(t.failedLogins ?? t.FailedLogins),
    lockouts: asNumber(t.lockouts ?? t.Lockouts),
    suspiciousLogins: asNumber(t.suspiciousLogins ?? t.SuspiciousLogins),
    unauthorized401: asNumber(t.unauthorized401 ?? t.Unauthorized401),
    forbidden403: asNumber(t.forbidden403 ?? t.Forbidden403),
    unauthorizedApiCalls: asNumber(t.unauthorizedApiCalls ?? t.UnauthorizedApiCalls),
    sensitiveRouteHits: asNumber(t.sensitiveRouteHits ?? t.SensitiveRouteHits),
    massDownloadAlerts: asNumber(t.massDownloadAlerts ?? t.MassDownloadAlerts),
    totalAlerts: asNumber(t.totalAlerts ?? t.TotalAlerts),
    activeAlerts: asNumber(t.activeAlerts ?? t.ActiveAlerts),
  };
}

function normalizeTrendPoint(row: unknown): SecurityDashboardTrendPoint {
  const t = (row && typeof row === 'object' ? (row as Record<string, unknown>) : {}) as Record<string, unknown>;
  return {
    bucketStartUtc: asString(t.bucketStartUtc ?? t.BucketStartUtc),
    failedLogins: asNumber(t.failedLogins ?? t.FailedLogins),
    lockouts: asNumber(t.lockouts ?? t.Lockouts),
    suspiciousLogins: asNumber(t.suspiciousLogins ?? t.SuspiciousLogins),
    unauthorizedApiCalls: asNumber(t.unauthorizedApiCalls ?? t.UnauthorizedApiCalls),
    sensitiveRouteHits: asNumber(t.sensitiveRouteHits ?? t.SensitiveRouteHits),
    alerts: asNumber(t.alerts ?? t.Alerts),
  };
}

export function normalizeSecurityDashboard(result: unknown): SecurityDashboard {
  const r = (result && typeof result === 'object' ? (result as Record<string, unknown>) : {}) as Record<
    string,
    unknown
  >;
  const rawTrend = r.trend ?? r.Trend;
  return {
    fromUtc: asString(r.fromUtc ?? r.FromUtc),
    toUtc: asString(r.toUtc ?? r.ToUtc),
    companyId: asOptionalString(r.companyId ?? r.CompanyId),
    bucketMinutes: asNumber(r.bucketMinutes ?? r.BucketMinutes),
    totals: normalizeTotals(r.totals ?? r.Totals),
    authEventCounts: asRecord(r.authEventCounts ?? r.AuthEventCounts),
    apiDeniedCountsByStatus: asRecord(r.apiDeniedCountsByStatus ?? r.ApiDeniedCountsByStatus),
    alertCountsByType: asRecord(r.alertCountsByType ?? r.AlertCountsByType),
    alertCountsBySeverity: asRecord(r.alertCountsBySeverity ?? r.AlertCountsBySeverity),
    trend: Array.isArray(rawTrend) ? rawTrend.map(normalizeTrendPoint) : [],
  };
}

export function normalizeSecurityAlertRow(row: unknown): SecurityAlertRow | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const a = row as Record<string, unknown>;
  const id = asString(a.id ?? a.Id).trim();
  if (!id) {
    return null;
  }
  return {
    id,
    alertType: asString(a.alertType ?? a.AlertType).trim(),
    severity: asString(a.severity ?? a.Severity).trim(),
    companyId: asOptionalString(a.companyId ?? a.CompanyId),
    merchantId: asOptionalString(a.merchantId ?? a.MerchantId),
    userId: asOptionalString(a.userId ?? a.UserId),
    correlationId: asOptionalString(a.correlationId ?? a.CorrelationId),
    detectedAt: asString(a.detectedAt ?? a.DetectedAt),
    resolvedAt: asOptionalString(a.resolvedAt ?? a.ResolvedAt),
    status: asAlertStatus(a.status ?? a.Status),
    metadataJson: asOptionalString(a.metadataJson ?? a.MetadataJson),
  };
}

function normalizePagedSecurityAlerts(result: unknown): PagedSecurityAlerts {
  if (!result || typeof result !== 'object') {
    return { items: [], totalCount: 0, page: 1, pageSize: 0 };
  }
  const r = result as Record<string, unknown>;
  const rawItems = r.items ?? r.Items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(normalizeSecurityAlertRow).filter((a): a is SecurityAlertRow => Boolean(a))
    : [];
  return {
    items,
    totalCount: asNumber(r.totalCount ?? r.TotalCount),
    page: asNumber(r.page ?? r.Page),
    pageSize: asNumber(r.pageSize ?? r.PageSize),
  };
}

function buildFilterParams(filters: SecurityDashboardFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.companyId?.trim()) {
    params.set('companyId', filters.companyId.trim());
  }
  if (filters.fromDate?.trim()) {
    params.set('fromUtc', filters.fromDate.trim());
  }
  if (filters.toDate?.trim()) {
    params.set('toUtc', filters.toDate.trim());
  }
  return params;
}

export async function fetchSecurityDashboard(
  getAccessToken: () => string | undefined,
  filters: SecurityDashboardFilters
): Promise<SecurityDashboard> {
  const params = buildFilterParams(filters);

  const result = await apiFetchJson<unknown>(`/api/admin/security/dashboard?${params.toString()}`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });

  return normalizeSecurityDashboard(result);
}

export async function fetchSecurityAlerts(
  getAccessToken: () => string | undefined,
  filters: SecurityDashboardFilters,
  status: SecurityAlertStatus,
  page: number,
  pageSize: number
): Promise<PagedSecurityAlerts> {
  const params = buildFilterParams(filters);
  params.set('status', status);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  const result = await apiFetchJson<unknown>(`/api/admin/security/alerts?${params.toString()}`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });

  return normalizePagedSecurityAlerts(result);
}
